import { FeedbackState, IConversationMessage, IConversationMessageVariation } from './conversation';

/**
 * Feedback state for every response variation the user has rated, keyed by the
 * variation's uuid. Exposed as a read-only map: owners publish a *new* map on
 * every transition so `OnPush` consumers see a changed input reference.
 */
export type FeedbackStates = ReadonlyMap<string, FeedbackState>;

function selectedVariation(message: IConversationMessage): IConversationMessageVariation | undefined {
    return message?.variations?.[message.selectedVariationIndex];
}

function stateFor(states: FeedbackStates, message: IConversationMessage): FeedbackState | undefined {
    const uuid = selectedVariation(message)?.uuid;
    return uuid == null ? undefined : states.get(uuid);
}

/** The initial thumbs up/down submit is still in flight — both thumbs are disabled. */
export function isFeedbackLoading(states: FeedbackStates, message: IConversationMessage): boolean {
    return stateFor(states, message) === 'loading';
}

/**
 * The exchange is finished: the thumb landed and the optional details were either
 * submitted or dismissed. While the details form is still open (`initial-success`) the
 * form stands in for this notification rather than sitting above it.
 */
export function isFeedbackSuccessful(states: FeedbackStates, message: IConversationMessage): boolean {
    const state = stateFor(states, message);
    if (state === undefined) {
        // Restored from history: the submission did succeed, just in an earlier session, so
        // the confirmation belongs on screen. Derived from the rating rather than seeded
        // into the state map, which the full page and the side panel own separately.
        return selectedVariation(message)?.feedbackReceived != null;
    }
    return state === 'success';
}

/** Either the thumb or the additional-details submit failed — show the failure notification. */
export function isFeedbackFailed(states: FeedbackStates, message: IConversationMessage): boolean {
    const state = stateFor(states, message);
    return state === 'initial-fail' || state === 'fail';
}

/**
 * Whether to show the additional-details form: right after the thumb is accepted,
 * and again after a failed details submit so the user can retry their text.
 */
export function showMessageFeedback(states: FeedbackStates, message: IConversationMessage): boolean {
    // Only a submission happening now opens this form: after a reload a rated message
    // shows its confirmation, and the form returns when the user picks a thumb again.
    const state = stateFor(states, message);
    return state === 'initial-success' || state === 'fail';
}
